import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

const WhatsAppButton = () => {
  const { settings } = useSiteSettings();
  const contactInfo = settings?.contact_info || {};
  const [showTooltip, setShowTooltip] = useState(false);
  const [showQuickChat, setShowQuickChat] = useState(false);

  const quickMessages = [
    'Hi! I need help planning a trip',
    'I want to know about packages',
    'I need a vehicle booking',
    'I want to speak to a travel expert',
  ];

  const handleSendMessage = (message) => {
    window.open(
      `https://wa.me/${(contactInfo.whatsapp || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`,
      '_blank'
    );
    setShowQuickChat(false);
  };

  return (
    <>
      {showQuickChat && (
        <div className="fixed bottom-24 right-4 md:right-8 z-50 w-[300px] md:w-[350px] bg-s-bg2 rounded-2xl shadow-2xl border border-s-border overflow-hidden" data-testid="whatsapp-quick-chat">
          <div className="bg-green-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Sentora Holidays Support</p>
                <p className="text-green-100 text-xs">Online • Typically replies instantly</p>
              </div>
            </div>
            <button onClick={() => setShowQuickChat(false)} className="text-white/70 hover:text-white">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <div className="bg-green-50 text-gray-800 p-3 rounded-lg rounded-tl-none text-sm max-w-[85%]">
              Hi! Welcome to Sentora Holidays. How can we help you today?
            </div>
            <p className="text-s-muted text-xs text-center">Choose a message or type your own</p>
            <div className="space-y-2">
              {quickMessages.map((msg, idx) => (
                <button key={idx} onClick={() => handleSendMessage(msg)} className="w-full text-left text-sm px-4 py-3 rounded-xl bg-s-card hover:bg-s-card/70 text-s-text2 hover:text-s-text transition-colors border border-s-border">
                  {msg}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 z-50 flex flex-col items-end gap-3">
        {showTooltip && !showQuickChat && (
          <div className="bg-s-bg2 text-s-text px-4 py-2 rounded-lg shadow-lg text-sm border border-s-border animate-fade-in">
            Chat with us!
          </div>
        )}
        <button
          data-testid="whatsapp-fab"
          onClick={() => setShowQuickChat(!showQuickChat)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="bg-green-500 hover:bg-green-600 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
          aria-label="Chat on WhatsApp"
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-8 md:h-8 fill-white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </button>
      </div>
    </>
  );
};

export default WhatsAppButton;
